import { useEffect, useRef, useState } from "react";
import { Download, X, Play, Volume2, VolumeX, Clapperboard, Search, RotateCcw, Folder } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl, BACKEND, formatApiError } from "../../lib/api";

const fmtSn = (s) => `${Math.round(s || 0)} sn`;
const fmtMb = (b) => `${((b || 0) / 1048576).toFixed(2)} MB`;

function VideoCard({ video, categoryName, onOpen }) {
  const ref = useRef(null);
  const [volume, setVolume] = useState(0.6);

  const enter = () => {
    const v = ref.current;
    if (v) {
      v.volume = volume;
      v.muted = volume === 0;
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };
  const leave = () => {
    const v = ref.current;
    if (v) v.pause();
  };
  const changeVol = (e) => {
    const val = Number(e.target.value);
    setVolume(val);
    const v = ref.current;
    if (v) {
      v.volume = val;
      v.muted = val === 0;
    }
  };

  return (
    <div data-testid={`video-card-${video.id}`}
      className="rounded-xl border border-white/5 bg-[#0A0E17] overflow-hidden transition-colors duration-300 hover:border-[#00F0FF]/50 hover:shadow-[0_0_20px_rgba(0,240,255,0.15)] group">
      <div className="relative aspect-video bg-black cursor-pointer" onClick={() => onOpen(video)} onMouseEnter={enter} onMouseLeave={leave}>
        <video ref={ref} src={imageUrl(video.url)} className="absolute inset-0 w-full h-full object-contain" loop playsInline preload="metadata" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        <Play size={40} fill="currentColor" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg pointer-events-none" />
        <span data-testid={`video-duration-${video.id}`} className="absolute bottom-3 right-3 bg-black/80 text-white text-xs px-2 py-1 rounded backdrop-blur-sm font-display">
          {fmtSn(video.duration)}
        </span>
        <div
          className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {volume === 0 ? <VolumeX size={13} className="text-white/80 shrink-0" /> : <Volume2 size={13} className="text-white/80 shrink-0" />}
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={changeVol}
            data-testid={`video-volume-${video.id}`}
            aria-label="Ses seviyesi"
            className="w-16 h-1 accent-cyan-400 cursor-pointer"
          />
        </div>
        <a
          href={`${BACKEND}/api/member/videos/${video.id}/download`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`video-download-${video.id}`}
          aria-label="İndir"
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-[#0A0E17]/80 hover:bg-[#00F0FF]/20 border border-white/10 hover:border-[#00F0FF]/50 text-white p-2 rounded-full backdrop-blur-sm transition-colors z-20"
        >
          <Download size={15} />
        </a>
      </div>
      <div className="p-3.5">
        <p className="text-xs font-semibold text-white truncate">{video.title || "Video"}</p>
        <p className="text-[10px] text-[#64748B] mt-0.5 truncate">{categoryName || "Kategorisiz"} · {fmtMb(video.size)} · {fmtSn(video.duration)}</p>
        <div className="flex gap-2 mt-3">
          <button onClick={() => onOpen(video)} data-testid={`video-watch-${video.id}`}
            className="btn-glow flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-white py-2 rounded-full">
            <Play size={12} fill="currentColor" /> İzle
          </button>
          <a href={`${BACKEND}/api/member/videos/${video.id}/download`} data-testid={`video-download-btn-${video.id}`}
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 transition-colors rounded-full px-4 py-2">
            <Download size={12} /> İndir
          </a>
        </div>
      </div>
    </div>
  );
}

export default function VideoArchive() {
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    api.get("/member/videos")
      .then((r) => {
        setCategories(r.data.categories);
        setVideos(r.data.videos);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "";
  const q = search.trim().toLowerCase();
  const filtered = q ? videos.filter((v) => (v.title || "").toLowerCase().includes(q)) : videos;

  const sections = [];
  if (activeCat === "all") {
    for (const c of categories) {
      const vids = filtered.filter((v) => v.category_id === c.id);
      if (vids.length) sections.push({ id: c.id, name: c.name, videos: vids });
    }
    const uncats = filtered.filter((v) => !v.category_id || !categories.some((c) => c.id === v.category_id));
    if (uncats.length) sections.push({ id: "uncat", name: "Diğer", videos: uncats });
  } else {
    const vids = filtered.filter((v) => v.category_id === activeCat);
    sections.push({ id: activeCat, name: catName(activeCat), videos: vids });
  }

  const resetFilters = () => {
    setSearch("");
    setActiveCat("all");
  };

  return (
    <div data-testid="video-archive">
      <p className="text-sm text-[#64748B] mb-6 -mt-4" data-testid="video-archive-stats">
        Toplam {videos.length} video dosyası, {categories.length} kategori
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Video adı ara..."
            data-testid="video-search-input"
            maxLength={80}
            className="w-full bg-[#0A0E17] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors"
          />
        </div>
        <div className="flex gap-3 sm:justify-end">
          <select
            value={activeCat}
            onChange={(e) => setActiveCat(e.target.value)}
            data-testid="video-category-filter"
            className="bg-[#0A0E17] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60 transition-colors cursor-pointer"
          >
            <option value="all">Tüm Kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button onClick={resetFilters} data-testid="video-filter-reset"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A0ABC0] border border-white/10 bg-[#0A0E17] hover:border-cyan-400/40 hover:text-cyan-200 transition-colors rounded-xl px-4 py-2.5">
            <RotateCcw size={13} /> Sıfırla
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#64748B] py-10">Yükleniyor...</p>
      ) : sections.every((s) => s.videos.length === 0) || sections.length === 0 ? (
        <div data-testid="video-archive-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-20 text-center">
          <Clapperboard size={32} strokeWidth={1.5} className="text-[#64748B] mx-auto mb-4" />
          <p className="text-[#A0ABC0] text-sm">{q ? "Aramayla eşleşen video bulunamadı." : "Henüz video eklenmemiş. Çok yakında!"}</p>
        </div>
      ) : (
        sections.map((sec) => (
          <div key={sec.id} className="rounded-2xl border border-white/[0.07] bg-[#0A0E17]/60 overflow-hidden mb-6" data-testid={`video-section-${sec.id}`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#0047FF] to-[#00F0FF] flex items-center justify-center shrink-0">
                  <Folder size={15} className="text-white" />
                </div>
                <h2 className="font-display text-sm font-bold text-white">{sec.name}</h2>
              </div>
              <span className="text-[11px] font-semibold text-cyan-300 bg-cyan-400/10 border border-cyan-400/25 rounded-full px-3 py-1" data-testid={`video-section-count-${sec.id}`}>
                {sec.videos.length} dosya
              </span>
            </div>
            {sec.videos.length === 0 ? (
              <p className="text-xs text-[#64748B] px-5 py-8 text-center">Bu kategoride eşleşen video yok.</p>
            ) : (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sec.videos.map((v) => (
                  <VideoCard key={v.id} video={v} categoryName={catName(v.category_id)} onOpen={setOpenVideo} />
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {openVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl flex flex-col items-center justify-center p-4 lg:p-12"
          data-testid="video-modal"
          onClick={() => setOpenVideo(null)}
        >
          <button
            onClick={() => setOpenVideo(null)}
            data-testid="video-modal-close"
            aria-label="Kapat"
            className="absolute top-6 right-6 text-white/50 hover:text-white p-3 z-[110] bg-white/5 hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          <div
            className="relative w-full max-w-3xl bg-black rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(0,240,255,0.1)] border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <video src={imageUrl(openVideo.url)} className="w-full max-h-[70vh] object-contain" controls autoPlay data-testid="video-modal-player" />
          </div>
          <p className="text-sm text-white/80 mt-4 font-display" data-testid="video-modal-title">{openVideo.title || "Video"}</p>
          <a
            href={`${BACKEND}/api/member/videos/${openVideo.id}/download`}
            data-testid="video-modal-download"
            onClick={(e) => e.stopPropagation()}
            className="mt-4 px-8 py-3.5 rounded-full bg-[#0A0E17] border border-[#00F0FF]/30 text-white hover:bg-[#00F0FF]/10 transition-colors flex items-center gap-2 font-display text-sm tracking-wide"
          >
            <Download size={16} /> Videoyu İndir
          </a>
        </div>
      )}
    </div>
  );
}
